import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDepressionMode } from '../contexts/DepressionModeContext';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import SymptomsSection from '../components/SymptomsSection';
import LifestyleSection from '../components/LifestyleSection';
import AllergiesSection from '../components/AllergiesSection';
import ImageUploadSection from '../components/ImageUploadSection';
import api from '../api/api';
import '../styles/pages/FormPageV2.css';

/**
 * FormPageV2 - つむぎデザインv1.0統一版
 * - 2回目以降の入力短縮機能
 * - シンプルで優しいUI
 */
const FormPageV2 = () => {
  const { user, plan } = useAuth();
  const { depressionMode } = useDepressionMode();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // ユーザープロフィール
  const [userProfile, setUserProfile] = useState(null);

  // 前回の相談データ（2回目以降の自動復元用）
  const [lastConsultation, setLastConsultation] = useState(null);

  // プリセット質問選択
  const [selectedPreset, setSelectedPreset] = useState('');

  // 詳細セクション展開状態
  const [showDetailedInput, setShowDetailedInput] = useState(false);

  // フォームデータ（元のFormPageと同じ構造）
  const [formData, setFormData] = useState({
    basicInfo: {
      displayName: '',
      age: '',
      gender: '',
      height: '',
      weight: '',
      livingStatus: ''
    },
    symptoms: {},
    lifestyle: {},
    allergies: {},
    consultation: '',
    labValues: {}
  });

  // 画像アップロード関連
  const [ocrResult, setOcrResult] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [ocrCompleted, setOcrCompleted] = useState(false);

  const [result, setResult] = useState(null);

  const isPremium = plan === 'premium';

  // プリセット質問選択肢（ペルソナ分析フィードバック反映版 + おやすみモード対応）
  const presetQuestions = [
    // おやすみモード専用：何も考えたくない時用
    ...(depressionMode ? [{
      value: 'just_rest',
      label: '何も考えたくない・ただ休みたい',
      icon: '🌙',
      badge: 'おやすみモード',
      badgeColor: 'bg-blue-100 text-blue-600',
      prompt: '今日は何も考えたくないです。最低限だけ教えてください。'
    }] : []),
    {
      value: 'morning_fatigue',
      label: '朝起きられない・一日中だるい',
      icon: '😴',
      badge: '最も多い悩み',
      badgeColor: 'bg-red-100 text-red-800',
      prompt: '朝起きるのがつらくて、一日中体が重くてだるいです。少しでも元気になれる食べ物を教えてください。'
    },
    {
      value: 'appetite_loss',
      label: '食欲がない・食べるのが億劫',
      icon: '🍽️',
      badge: '人気',
      badgeColor: 'bg-orange-100 text-orange-800',
      prompt: '食欲がなくて、食事をするのが億劫です。簡単に栄養を取れる方法を教えてください。'
    },
    {
      value: 'mental_health',
      label: '気分が優れない・元気が出ない',
      icon: '😔',
      badge: 'メンタルケア',
      badgeColor: 'bg-blue-100 text-blue-800',
      prompt: '気分が優れなくて、元気が出ません。気分が楽になる食べ物ってありますか？'
    },
    {
      value: 'swelling_cold',
      label: 'むくみ・冷えがつらい',
      icon: '🧊',
      badge: '橋本病あるある',
      badgeColor: 'bg-purple-100 text-purple-800',
      prompt: 'むくみと冷えがひどくて辛いです。改善できる食事や生活習慣を教えてください。'
    },
    {
      value: 'simple_easy',
      label: '何を食べたらいいか分からない',
      icon: '🤷‍♀️',
      badge: '初めての方に',
      badgeColor: 'bg-green-100 text-green-800',
      prompt: 'バランスの良い食事って、具体的に何をどう食べればいいんですか？まず最初に何をすればいいか教えてください。'
    },
    {
      value: 'other',
      label: 'その他（自由に質問したい）',
      icon: '💭',
      badge: null,
      badgeColor: null,
      prompt: ''
    }
  ];

  // ページタイトル設定
  useEffect(() => {
    document.title = '栄養相談 | つむぎ';
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  // URLパラメータからプリセットを自動選択（つむぎチャットMode 3から遷移した場合）
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const presetParam = searchParams.get('preset');

    if (presetParam) {
      // プリセットが有効な値かチェック
      const validPreset = presetQuestions.find(q => q.value === presetParam);
      if (validPreset) {
        console.log('✅ URLパラメータからプリセットを自動選択:', presetParam);
        setSelectedPreset(presetParam);

        // URLからパラメータを削除（履歴を汚さない）
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [location.search]);

  const loadData = async () => {
    try {
      setLoading(true);

      // プロフィール情報を取得
      const profileResponse = await api.get('/api/profile');
      if (profileResponse.data.success && profileResponse.data.user) {
        setUserProfile(profileResponse.data.user);

        const profile = profileResponse.data.user.profile;
        if (profile) {
          setFormData(prev => ({
            ...prev,
            basicInfo: {
              displayName: profile.displayName || user?.name || '',
              age: profile.age || '',
              gender: profile.gender || '',
              height: profile.height || '',
              weight: profile.weight || '',
              livingStatus: profile.livingStatus || ''
            },
            allergies: {
              profileAllergies: Array.isArray(profile.allergies) ? profile.allergies : [],
              profileAllergiesOther: profile.allergiesOther || '',
              profileAvoidFoods: Array.isArray(profile.avoidFoods) ? profile.avoidFoods : [],
              profileAvoidFoodsOther: profile.avoidFoodsOther || ''
            }
          }));
        }
      }

      // 最新の相談履歴を取得し、自動的に症状・生活習慣を復元
      const consultationsResponse = await api.get('/api/consultations/last/consultation');
      if (consultationsResponse.data.success && consultationsResponse.data.consultation) {
        const lastConsult = consultationsResponse.data.consultation;
        setLastConsultation(lastConsult);

        // 2回目以降は自動的に症状・生活習慣・アレルギーを復元（プリセット選択は除く）
        if (lastConsult.formData) {
          setFormData(prev => ({
            ...prev,
            symptoms: lastConsult.formData.symptoms || {},
            lifestyle: lastConsult.formData.lifestyle || {},
            // アレルギー情報も前回のものを復元（プロフィールより優先）
            allergies: lastConsult.formData.allergies || prev.allergies
          }));

          // 前回データがある場合、詳細セクションを自動展開
          // ユーザーは毎回展開ボタンを押す必要がなくなる
          setShowDetailedInput(true);
        }
      }
    } catch (error) {
      console.error('データ読み込みエラー:', error);
    } finally {
      setLoading(false);
    }
  };


  // プリセット質問選択ハンドラー
  const handlePresetChange = (value) => {
    setSelectedPreset(value);
    const selected = presetQuestions.find(q => q.value === value);
    if (selected && selected.prompt) {
      setFormData(prev => ({ ...prev, consultation: selected.prompt }));
    } else if (value === 'other') {
      setFormData(prev => ({ ...prev, consultation: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 送信前バリデーション（エラーでもカウント消費しない）
    // 必須項目を3つに緩和: 年齢、性別、相談内容のみ

    // 年齢のバリデーション（必須）
    if (!formData.basicInfo.age || formData.basicInfo.age === '') {
      alert('年齢を入力してください😊\n\nより適切なアドバイスのために必要です');
      return;
    }

    const age = Number(formData.basicInfo.age);
    if (isNaN(age) || age < 1 || age > 120) {
      alert('年齢は1〜120の範囲で入力してください😊');
      return;
    }

    // 性別のバリデーション（必須）
    if (!formData.basicInfo.gender || formData.basicInfo.gender === '') {
      alert('性別を選択してください😊\n\nより適切なアドバイスのために必要です');
      return;
    }

    // 身長・体重のバリデーション（任意だが、入力されている場合は範囲チェック）
    if (formData.basicInfo.height && formData.basicInfo.height !== '') {
      const height = Number(formData.basicInfo.height);
      if (isNaN(height) || height < 100 || height > 250) {
        alert('身長は100〜250cm の範囲で入力してください😊');
        return;
      }
    }

    if (formData.basicInfo.weight && formData.basicInfo.weight !== '') {
      const weight = Number(formData.basicInfo.weight);
      if (isNaN(weight) || weight < 20 || weight > 300) {
        alert('体重は20〜300kg の範囲で入力してください😊');
        return;
      }
    }

    // 相談内容のバリデーション（必須、最低文字数制限なし）
    if (!formData.consultation.trim()) {
      alert('相談内容を入力してください😊\n\n気軽に、思ったことを書いてみてね');
      return;
    }

    try {
      setSubmitting(true);

      // Step 1: まずプロフィール情報を更新（基本情報+症状+生活習慣+アレルギー情報がデータベースと同期）
      try {
        const currentAllergies = formData.allergies?.currentAllergies || formData.allergies?.profileAllergies || [];
        const currentAllergiesOther = formData.allergies?.currentAllergiesOther || formData.allergies?.profileAllergiesOther || '';
        const currentAvoidFoods = formData.allergies?.currentAvoidFoods || formData.allergies?.profileAvoidFoods || [];
        const currentAvoidFoodsOther = formData.allergies?.currentAvoidFoodsOther || formData.allergies?.profileAvoidFoodsOther || '';

        const profileUpdate = {
          displayName: formData.basicInfo.displayName,
          age: formData.basicInfo.age,
          gender: formData.basicInfo.gender,
          height: formData.basicInfo.height,
          weight: formData.basicInfo.weight,
          livingStatus: formData.basicInfo.livingStatus,
          allergies: currentAllergies,
          allergiesOther: currentAllergiesOther,
          avoidFoods: currentAvoidFoods,
          avoidFoodsOther: currentAvoidFoodsOther
        };

        await api.put('/api/profile', profileUpdate);
      } catch (profileError) {
        console.error('プロフィール更新エラー:', profileError);
        // プロフィール更新に失敗しても相談は続行
      }

      // Step 2: 相談を送信
      console.log('📤 Sending consultation with presetType:', selectedPreset);
      const response = await api.post('/api/advice/generate', {
        formData: {
          basicInfo: formData.basicInfo,
          symptoms: formData.symptoms,
          lifestyle: formData.lifestyle,
          allergies: formData.allergies,
          consultation: formData.consultation,
          labValues: formData.labValues,
          presetType: selectedPreset || null
        },
        userInput: {
          displayName: formData.basicInfo.displayName,
          age: formData.basicInfo.age,
          gender: formData.basicInfo.gender,
          height: formData.basicInfo.height,
          weight: formData.basicInfo.weight,
          livingStatus: formData.basicInfo.livingStatus,
          labValues: formData.labValues
        }
      });

      if (response.data.success) {
        setResult(response.data);
        // 詳細ページにリダイレクト
        if (response.data.consultationId) {
          setTimeout(() => {
            navigate(`/consultation/${response.data.consultationId}`);
          }, 1500);
        }
      }
    } catch (error) {
      console.error('送信エラー:', error);

      // ユーザー向けの分かりやすいエラーメッセージ
      let userMessage = '';

      if (error.response?.status === 401) {
        userMessage = '⚠️ ログインが切れています\n\nもう一度ログインしてからお試しください';
      } else if (error.response?.status === 403) {
        if (error.response?.data?.code === 'MONTHLY_LIMIT_EXCEEDED') {
          userMessage = '📊 今月の相談回数の上限に達しました\n\nプランをアップグレードすると、さらに相談できるようになります';
        } else {
          userMessage = '⚠️ この操作は許可されていません\n\nログイン状態を確認してください';
        }
      } else if (error.response?.status === 429) {
        userMessage = '⏰ 少し時間をおいてからお試しください\n\n短時間に多くのリクエストがありました';
      } else if (error.response?.status >= 500) {
        userMessage = '🔧 サーバーで問題が発生しました\n\n時間をおいてからもう一度お試しください\n改善しない場合はお問い合わせください';
      } else if (!navigator.onLine) {
        userMessage = '📡 インターネット接続を確認してください\n\nオフラインのようです';
      } else if (error.response?.data?.message) {
        // バックエンドから具体的なメッセージがある場合
        userMessage = `⚠️ ${error.response.data.message}`;
      } else {
        userMessage = '⚠️ 相談の送信に失敗しました\n\nもう一度お試しいただくか、問題が続く場合はお問い合わせください';
      }

      alert(userMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <PageTitle
          title="栄養相談"
          subtitle="読み込み中..."
          mascot={<img src="/tsumugi3.png" alt="つむぎ" className="page-title__mascot-img" />}
        />
        <div className="tsumugi-container py-6">
          <div className="tsumugi-card text-center">
            <p className="text-hint">読み込み中...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (result) {
    return (
      <AppLayout>
        <PageTitle
          title="相談を送信しました"
          subtitle="つむぎがアドバイスを考えています"
          mascot={<img src="/tsumugi3.png" alt="つむぎ" className="page-title__mascot-img" />}
        />
        <div className="tsumugi-container py-6">
          <div className="tsumugi-card text-center">
            <div className="consultation-success">
              <div className="consultation-success__icon">✅</div>
              <p className="consultation-success__message">
                相談を受け付けました！<br />
                アドバイスページに移動します...
              </p>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageTitle
        title="栄養相談"
        subtitle="つむぎに相談してみよう"
        backPath="/mypage"
        backLabel="マイページ"
        mascot={<img src="/tsumugi3.png" alt="つむぎ" className="page-title__mascot-img" />}
      />

      {/* 送信中のローディングオーバーレイ */}
      {submitting && (
        <div className="form-page__loading-overlay">
          <div className="form-page__loading-mascot">
            <img src="/tsumugi.png" alt="つむぎ" className="form-page__loading-mascot-img" />
          </div>
          <h2 className="form-page__loading-title">
            つむぎがアドバイスを考えています...
          </h2>
          <p className="form-page__loading-description">
            あなたに最適な栄養アドバイスを<br/>
            丁寧に作成しています
          </p>
          <div className="form-page__loading-dots">
            <div className="form-page__loading-dot form-page__loading-dot--mint"></div>
            <div className="form-page__loading-dot form-page__loading-dot--peach"></div>
            <div className="form-page__loading-dot form-page__loading-dot--lilac"></div>
          </div>
          <p className="form-page__loading-note">
            💡 このまましばらくお待ちください（10〜30秒）
          </p>
        </div>
      )}

      <div className="tsumugi-container form-page">
        {/* おやすみモード時のメッセージ */}
        {depressionMode && (
          <div className="depression-mode-message">
            <span className="depression-mode-message__icon">🌙</span>
            今日は無理しなくていいよ。できる範囲で、ゆっくりで大丈夫。
          </div>
        )}

        {/* プリセット質問選択 - 最初に表示（A2: 相談→手続きの順序に変更） */}
        <div className="tsumugi-card preset-section">
          {!depressionMode && (
            <>
              <h3 className="tsumugi-card-title preset-section__title">今日はどうしましたか？</h3>
              <p className="text-hint preset-section__description">
                近いものを選んでね。相談文が自動で入力されます
              </p>
            </>
          )}
          {depressionMode && (
            <>
              <h3 className="tsumugi-card-title preset-section__title">今日の相談</h3>
              <p className="text-hint preset-section__description">
                これでいいよ、無理しないでね
              </p>
            </>
          )}

          <div className="preset-question-list">
            {/* おすすめ: 最初のプリセット */}
            {depressionMode ? (
              <div className="recommended-only">
                <div
                  key={presetQuestions[0].value}
                  className={`preset-question-card ${selectedPreset === presetQuestions[0].value ? 'preset-question-card--selected' : ''}`}
                  onClick={() => handlePresetChange(presetQuestions[0].value)}
                >
                  <input
                    type="radio"
                    name="consultation-preset"
                    value={presetQuestions[0].value}
                    checked={selectedPreset === presetQuestions[0].value}
                    onChange={() => {}}
                    className="form-page-v2__hidden-radio"
                  />

                  {presetQuestions[0].badge && (
                    <div className={`preset-question-badge ${presetQuestions[0].badgeColor}`}>
                      {presetQuestions[0].badge}
                    </div>
                  )}

                  {selectedPreset === presetQuestions[0].value && (
                    <div className="preset-question-check">✓</div>
                  )}

                  <div className="preset-question-content">
                    <div className="preset-question-icon">{presetQuestions[0].icon}</div>
                    <div className="preset-question-label">{presetQuestions[0].label}</div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {presetQuestions.map((preset) => (
                  <div
                    key={preset.value}
                    className={`preset-question-card ${selectedPreset === preset.value ? 'preset-question-card--selected' : ''}`}
                    onClick={() => handlePresetChange(preset.value)}
                  >
                    <input
                      type="radio"
                      name="consultation-preset"
                      value={preset.value}
                      checked={selectedPreset === preset.value}
                      onChange={() => {}}
                      className="form-page-v2__hidden-radio"
                    />

                    {preset.badge && (
                      <div className={`preset-question-badge ${preset.badgeColor}`}>
                        {preset.badge}
                      </div>
                    )}

                    {selectedPreset === preset.value && (
                      <div className="preset-question-check">✓</div>
                    )}

                    <div className="preset-question-content">
                      <div className="preset-question-icon">{preset.icon}</div>
                      <div className="preset-question-label">{preset.label}</div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* その他のプリセット（おやすみモードのみ、折りたたみ） */}
            {depressionMode && presetQuestions.length > 1 && (
              <details className="other-options form-page-v2__other-presets">
                <summary>他の相談を見る</summary>
                {presetQuestions.slice(1).map((preset) => (
                  <div
                    key={preset.value}
                    className={`preset-question-card ${selectedPreset === preset.value ? 'preset-question-card--selected' : ''} preset-question-card--in-details`}
                    onClick={() => handlePresetChange(preset.value)}
                  >
                    <input
                      type="radio"
                      name="consultation-preset"
                      value={preset.value}
                      checked={selectedPreset === preset.value}
                      onChange={() => {}}
                      className="form-page-v2__hidden-radio"
                    />

                    {preset.badge && (
                      <div className={`preset-question-badge ${preset.badgeColor}`}>
                        {preset.badge}
                      </div>
                    )}

                    {selectedPreset === preset.value && (
                      <div className="preset-question-check">✓</div>
                    )}

                    <div className="preset-question-content">
                      <div className="preset-question-icon">{preset.icon}</div>
                      <div className="preset-question-label">{preset.label}</div>
                    </div>
                  </div>
                ))}
              </details>
            )}
          </div>
        </div>

        {/* 基本情報カード - A2: 悩み選択の後に配置 / A3: 2回目以降は折りたたみ */}
        <div className="tsumugi-card tsumugi-card--peach basic-info-card">
          {lastConsultation ? (
            // 2回目以降: 折りたたみ表示
            <details className="basic-info-card__details">
              <summary className="basic-info-card__summary">
                <h3 className="tsumugi-card-title basic-info-card__title">📋 あなたの情報</h3>
                <span className="text-hint">前回の情報を使用中（タップで編集）</span>
              </summary>
              <div className="basic-info-card__form">
                <div>
                  <label className="profile-edit-label basic-info-card__label">お名前（ニックネーム可）</label>
                  <input
                    type="text"
                    className="profile-edit-input basic-info-card__input"
                    value={formData.basicInfo.displayName}
                    onChange={(e) => setFormData({
                      ...formData,
                      basicInfo: { ...formData.basicInfo, displayName: e.target.value }
                    })}
                    placeholder="例: ゆうこ"
                  />
                </div>

                <div>
                  <label className="profile-edit-label basic-info-card__label">年齢</label>
                  <input
                    type="number"
                    className="profile-edit-input basic-info-card__input"
                    value={formData.basicInfo.age}
                    onChange={(e) => setFormData({
                      ...formData,
                      basicInfo: { ...formData.basicInfo, age: e.target.value }
                    })}
                    placeholder="例: 45"
                  />
                </div>

                <div>
                  <label className="profile-edit-label basic-info-card__label">性別</label>
                  <select
                    className="profile-edit-select basic-info-card__select"
                    value={formData.basicInfo.gender}
                    onChange={(e) => setFormData({
                      ...formData,
                      basicInfo: { ...formData.basicInfo, gender: e.target.value }
                    })}
                  >
                    <option value="">選択してください</option>
                    <option value="female">女性</option>
                    <option value="male">男性</option>
                    <option value="other">その他</option>
                  </select>
                </div>

                <div className="basic-info-card__row">
                  <div>
                    <label className="profile-edit-label basic-info-card__label">身長(cm) <span className="text-hint">（任意）</span></label>
                    <input
                      type="number"
                      className="profile-edit-input basic-info-card__input"
                      value={formData.basicInfo.height}
                      onChange={(e) => setFormData({
                        ...formData,
                        basicInfo: { ...formData.basicInfo, height: e.target.value }
                      })}
                      placeholder="160"
                    />
                  </div>
                  <div>
                    <label className="profile-edit-label basic-info-card__label">体重(kg) <span className="text-hint">（任意）</span></label>
                    <input
                      type="number"
                      className="profile-edit-input basic-info-card__input"
                      value={formData.basicInfo.weight}
                      onChange={(e) => setFormData({
                        ...formData,
                        basicInfo: { ...formData.basicInfo, weight: e.target.value }
                      })}
                      placeholder="55"
                    />
                  </div>
                </div>

                <div className="basic-info-card__field">
                  <label className="profile-edit-label basic-info-card__label">生活状況 <span className="text-hint">（任意）</span></label>
                  <select
                    className="profile-edit-select basic-info-card__select"
                    value={formData.basicInfo.livingStatus}
                    onChange={(e) => setFormData({
                      ...formData,
                      basicInfo: { ...formData.basicInfo, livingStatus: e.target.value }
                    })}
                  >
                    <option value="">選択してください</option>
                    <option value="alone">一人暮らし</option>
                    <option value="family-small">家族と同居（2-3人）</option>
                    <option value="family-large">家族と同居（4人以上）</option>
                  </select>
                  <p className="text-hint basic-info-card__hint">
                    生活状況に合わせた食事量・買い物リストを提案します
                  </p>
                </div>
              </div>
            </details>
          ) : (
            // 初回: 展開表示
            <>
              <h3 className="tsumugi-card-title basic-info-card__title">📋 あなたの情報</h3>
              <p className="text-hint basic-info-card__hint">
                💡 入力した情報は保存され、次回の相談でも使用されます
              </p>
              <div className="basic-info-card__form">
                <div>
                  <label className="profile-edit-label basic-info-card__label">お名前（ニックネーム可）</label>
                  <input
                    type="text"
                    className="profile-edit-input basic-info-card__input"
                    value={formData.basicInfo.displayName}
                    onChange={(e) => setFormData({
                      ...formData,
                      basicInfo: { ...formData.basicInfo, displayName: e.target.value }
                    })}
                    placeholder="例: ゆうこ"
                  />
                </div>

                <div>
                  <label className="profile-edit-label basic-info-card__label">年齢</label>
                  <input
                    type="number"
                    className="profile-edit-input basic-info-card__input"
                    value={formData.basicInfo.age}
                    onChange={(e) => setFormData({
                      ...formData,
                      basicInfo: { ...formData.basicInfo, age: e.target.value }
                    })}
                    placeholder="例: 45"
                  />
                </div>

                <div>
                  <label className="profile-edit-label basic-info-card__label">性別</label>
                  <select
                    className="profile-edit-select basic-info-card__select"
                    value={formData.basicInfo.gender}
                    onChange={(e) => setFormData({
                      ...formData,
                      basicInfo: { ...formData.basicInfo, gender: e.target.value }
                    })}
                  >
                    <option value="">選択してください</option>
                    <option value="female">女性</option>
                    <option value="male">男性</option>
                    <option value="other">その他</option>
                  </select>
                </div>

                <div className="basic-info-card__row">
                  <div>
                    <label className="profile-edit-label basic-info-card__label">身長(cm) <span className="text-hint">（任意）</span></label>
                    <input
                      type="number"
                      className="profile-edit-input basic-info-card__input"
                      value={formData.basicInfo.height}
                      onChange={(e) => setFormData({
                        ...formData,
                        basicInfo: { ...formData.basicInfo, height: e.target.value }
                      })}
                      placeholder="160"
                    />
                  </div>
                  <div>
                    <label className="profile-edit-label basic-info-card__label">体重(kg) <span className="text-hint">（任意）</span></label>
                    <input
                      type="number"
                      className="profile-edit-input basic-info-card__input"
                      value={formData.basicInfo.weight}
                      onChange={(e) => setFormData({
                        ...formData,
                        basicInfo: { ...formData.basicInfo, weight: e.target.value }
                      })}
                      placeholder="55"
                    />
                  </div>
                </div>

                <div className="basic-info-card__field">
                  <label className="profile-edit-label basic-info-card__label">生活状況 <span className="text-hint">（任意）</span></label>
                  <select
                    className="profile-edit-select basic-info-card__select"
                    value={formData.basicInfo.livingStatus}
                    onChange={(e) => setFormData({
                      ...formData,
                      basicInfo: { ...formData.basicInfo, livingStatus: e.target.value }
                    })}
                  >
                    <option value="">選択してください</option>
                    <option value="alone">一人暮らし</option>
                    <option value="family-small">家族と同居（2-3人）</option>
                    <option value="family-large">家族と同居（4人以上）</option>
                  </select>
                  <p className="text-hint basic-info-card__hint">
                    生活状況に合わせた食事量・買い物リストを提案します
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* 相談フォーム */}
        <form onSubmit={handleSubmit}>
          {/* 質問入力 - 「その他」選択時のみ表示 */}
          {selectedPreset === 'other' && (
            <div className="tsumugi-card consultation-input-card">
              <h3 className="tsumugi-card-title consultation-input-card__title">💬 相談内容を入力してください</h3>
              <p className="text-hint consultation-input-card__description">
                具体的に書くと、より詳しいアドバイスがもらえます
              </p>
              <textarea
                value={formData.consultation}
                onChange={(e) => setFormData({ ...formData, consultation: e.target.value })}
                placeholder="例: 朝起きられなくて困っています。何を食べたら元気になれますか？"
                className="profile-edit-textarea w-full consultation-input-card__textarea"
                rows="6"
                maxLength={2000}
                required
              />
              <p className="profile-edit-hint consultation-input-card__counter">
                {formData.consultation.length} / 2000文字
              </p>
            </div>
          )}

          {/* アコーディオン展開ボタン - スマホ最適化（おやすみモードでは非表示） */}
          {!depressionMode && (
            <div className="tsumugi-card tsumugi-card--mint p-0 form-accordion-wrapper">
              <div
                className={`form-accordion ${showDetailedInput ? 'form-accordion--open' : ''}`}
                onClick={() => setShowDetailedInput(!showDetailedInput)}
              >
              <div className="form-accordion__header">
                <div className="form-accordion__content">
                  <h3 className="form-accordion__title">
                    {showDetailedInput ? '▼' : '▶'} より詳しいアドバイスが欲しい方へ（任意）
                  </h3>
                  {ocrCompleted && !showDetailedInput && (
                    <p className="form-accordion__subtitle">
                      <span className="form-accordion__status-badge">
                        🩸 血液検査完了
                      </span>
                    </p>
                  )}
                  <p className="form-accordion__subtitle">
                    タップして{showDetailedInput ? '閉じる' : '開く'}
                  </p>
                </div>
                <div className="form-accordion__icon">
                  {showDetailedInput ? '−' : '+'}
                </div>
              </div>
            </div>

            {showDetailedInput && (
              <div
                className="form-accordion__body"
                onClick={(e) => e.stopPropagation()}
              >
                <p className="form-accordion__benefits">
                  ✅ より詳しく教えていただくと...<br />
                  <span className="form-accordion__benefits-list">
                    ・ より具体的な食材<br />
                    ・ あなた専用のレシピ<br />
                    ・ 症状別の栄養アドバイス<br />
                    がもらえます
                  </span>
                </p>

                {/* SymptomsSection */}
                <SymptomsSection
                  formData={formData}
                  setFormData={setFormData}
                />

                {/* LifestyleSection */}
                <LifestyleSection
                  formData={formData}
                  setFormData={setFormData}
                />

                {/* AllergiesSection */}
                <AllergiesSection
                  formData={formData}
                  setFormData={setFormData}
                />

                {/* 血液検査データ - アコーディオン内に移動 */}
                <div className="blood-test-section">
                  <h3 className="tsumugi-card-title blood-test-section__title">🩸 血液検査データ（任意）</h3>
                  <p className="text-hint blood-test-section__description">
                    血液検査の結果をお持ちの方は、より詳細な栄養アドバイスが受けられます
                  </p>
              <ImageUploadSection
                imageFile={imageFile}
                setImageFile={setImageFile}
                labValues={formData.labValues}
                setLabValues={(values) => {
                  setFormData(prev => ({
                    ...prev,
                    labValues: values
                  }));
                }}
                ocrResult={ocrResult}
                setOcrResult={setOcrResult}
                ocrCompleted={ocrCompleted}
                setOcrCompleted={setOcrCompleted}
                onOcrComplete={() => {
                  setShowDetailedInput(false);
                  // 送信ボタンの位置にスクロール
                  setTimeout(() => {
                    const submitButton = document.querySelector('.form-actions');
                    if (submitButton) {
                      submitButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }, 300);
                }}
              />
                </div>
              </div>
            )}
            </div>
          )}

          {/* 送信ボタン - スマホ最適化 */}
          <div className="form-actions">
            <button
              type="submit"
              className="tsumugi-btn tsumugi-btn-primary w-full form-actions__submit"
              disabled={submitting}
            >
              {submitting ? '送信中...' : depressionMode ? '🌙 つむぎに話す' : '✨ 栄養アドバイスをもらう'}
            </button>
          </div>
        </form>

        {/* 注意事項 - スマホ最適化 */}
        <div className="tsumugi-card tsumugi-card--lilac form-notice">
          <h3 className="tsumugi-card-title form-notice__title">💡 ご利用にあたって</h3>
          <div className="profile-edit-notes">
            <p className="profile-edit-note form-notice__note">
              📌 このサービスは医療行為ではありません
            </p>
            <p className="profile-edit-note form-notice__note">
              🔒 相談内容はプライバシー保護されます
            </p>
            <p className="profile-edit-note form-notice__note">
              ✨ 診断や治療が必要な場合は、必ず医師にご相談ください
            </p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default FormPageV2;
